// ONE IDEMPOTENCY KEY PER ATTEMPT AT A LETTER (WEB-RESUME, R242).
//
// The server reads `idempotencyKey` as "the same tap": a repeat within ten minutes is answered with the generation the
// first one queued and queues nothing (`functions/src/v2/letter/queue.ts` `idempotentReplay`). So the key must name
// ONE ATTEMPT — not one render, not one click:
//   - a retry of a request whose answer never arrived (the network dropped it, the body could not be read) sends the
//     SAME key, so if the server did queue it the retry is answered with that letter instead of starting a second one;
//   - once the server has answered — it queued, joined or replayed a letter, or refused with its own sentence — the
//     attempt is over, and the next press is a NEW key. That keeps WEB-PARITY-5's F-WP5-1 fix true: "Build my letter"
//     pressed again after a letter that failed, or "Revise from your details" soon after a first letter, is a new
//     generation, never the old one replayed for ten minutes.
//
// HELD BY THIS PAGE, per appeal and per kind of request, and nowhere else: it outlives a re-render and the screen being
// left and opened again, and dies with a reload. A reload needs nothing from here: the appeal's snapshot names the
// letter it is waiting on (`letter.latestGenerationAt`), which the Letter step reads back, and with the server's
// one-job switch on a second request joins the first whatever key it carries.

import type { V2Result } from "@contract";

export type AttemptMode = "build" | "revise";

const open = new Map<string, string>();

const slotOf = (appealId: string, mode: AttemptMode): string => `${mode}:${appealId}`;

/** The key of the attempt in progress for this appeal, or a new attempt's. */
export function attemptKey(appealId: string, mode: AttemptMode, nowMs: number = Date.now()): string {
  const slot = slotOf(appealId, mode);
  const held = open.get(slot);
  if (held !== undefined) return held;
  const key = mode === "build" ? `web-${appealId}-${nowMs}` : `web-revise-${appealId}-${nowMs}`;
  open.set(slot, key);
  return key;
}

/** The request came back. The attempt ends when the SERVER answered it — a letter's address or a refusal in its own
 *  words; a request that never reached it, or whose answer could not be read, is still the same attempt. */
export function attemptAnswered(appealId: string, mode: AttemptMode, result: V2Result<unknown>): void {
  if (result.ok || result.kind === "v2Error") open.delete(slotOf(appealId, mode));
}

/** Test-only: a fresh page. */
export function forgetAttempts(): void {
  open.clear();
}
